import { Loader2, MoreVertical, Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
interface Topic {
  id: string;
  title: string;
  description?: string;
  icon?: string;
  created_at: string;
}
interface SidebarTopicsProps {
  topics: Topic[];
  searchQuery: string;
  onTopicClick: (topicId: string) => void;
  onTopicRename?: (topicId: string) => void;
  onTopicDelete?: (topicId: string) => void;
  loading?: boolean;
  activeTopicId?: string;
}
const SidebarTopics = ({
  topics,
  searchQuery,
  onTopicClick,
  onTopicRename,
  onTopicDelete,
  loading = false,
  activeTopicId
}: SidebarTopicsProps) => {

  // Filter topics based on search query
  const filteredTopics = topics.filter(topic => topic.title.toLowerCase().includes(searchQuery.toLowerCase()) || topic.description && topic.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const renderTopic = (topic: Topic) => {
    const isActive = topic.id === activeTopicId;
    const containerCls = `flex items-center justify-between pl-3 pr-0 py-2 rounded-lg transition-all duration-200 cursor-pointer group ${isActive ? "bg-white shadow-sm" : "hover:bg-white/50"}`;
    
    return (
      <div 
        key={topic.id} 
        className={containerCls} 
        aria-current={isActive ? "page" : undefined} 
        data-active={isActive ? "true" : "false"}
      >
        <div onClick={() => onTopicClick(topic.id)} className="flex items-center gap-3 flex-1 min-w-0">
          <span className={`text-sm font-normal truncate ${isActive ? "text-foreground" : "text-foreground/80"}`}>
            {topic.title}
          </span>
        </div>
        <div className="flex items-center gap-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="p-1 w-6 h-6 hover:bg-gray-100 rounded-lg transition-all duration-200 border-0 focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:outline-none opacity-0 group-hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <MoreVertical className="w-3 h-3 text-foreground/70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40 bg-white border shadow-lg z-50">
              <DropdownMenuItem 
                onClick={(e) => {
                  e.stopPropagation();
                  onTopicRename?.(topic.id);
                }}
                className="cursor-pointer"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Update
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => {
                  e.stopPropagation();
                  onTopicDelete?.(topic.id);
                }}
                className="cursor-pointer text-red-600 focus:text-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };
  return (
    <div className="space-y-1 pb-4">
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
          <span className="ml-2 text-sm text-slate-500">Loading topics...</span>
        </div>
      ) : filteredTopics.length === 0 ? (
        <p className="text-sm text-slate-500 px-2">
          {searchQuery ? "No topics found" : "No topics yet"}
        </p>
      ) : (
        filteredTopics.map(renderTopic)
      )}
    </div>
  );
};
export default SidebarTopics;